import { Button, Divider, IconButton, Paper, Stack } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { SafeDateTimeRangePicker } from "components/SafeDateTimeRangePicker";
import { useCallback, useMemo } from "react";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";
import { TimezoneToggleButton } from "components/scrapEyeDataDashboard/components/TimezoneToggleButton";

const WINDOW_PRESETS = [
  { id: "hour", labelKey: "oneHour", durationMs: 60 * 60 * 1000 },
  { id: "day", labelKey: "oneDay", durationMs: 24 * 60 * 60 * 1000 },
  { id: "week", labelKey: "oneWeek", durationMs: 7 * 24 * 60 * 60 * 1000 },
  { id: "month", labelKey: "oneMonth", durationMs: 30 * 24 * 60 * 60 * 1000 },
] as const;

const SHIFT_FRACTION = 0.3;

interface Props {
  readonly pickerValue: [Dayjs | null, Dayjs | null];
  readonly onRangeChange: (value: [Dayjs | null, Dayjs | null]) => void;
}

export default function DateTimeControls({
  pickerValue,
  onRangeChange,
}: Props) {
  const resolvedTimeZone = useResolvedScrapViewportTimeZone();
  const { t } = useTenantTranslation();

  const start = useMemo(
    () => pickerValue[0]?.toDate() ?? new Date(),
    [pickerValue]
  );
  const end = useMemo(
    () => pickerValue[1]?.toDate() ?? new Date(),
    [pickerValue]
  );

  const handleShiftWindow = useCallback(
    (direction: -1 | 1) => {
      if (!pickerValue[0] || !pickerValue[1]) return;
      const windowMs = end.getTime() - start.getTime();
      const deltaMs = windowMs * SHIFT_FRACTION * direction;
      const newStart = dayjs(start.getTime() + deltaMs);
      const newEnd = dayjs(end.getTime() + deltaMs);
      onRangeChange([newStart, newEnd]);
    },
    [pickerValue, start, end, onRangeChange]
  );

  const handleJumpToNow = useCallback(() => {
    if (!pickerValue[0] || !pickerValue[1]) return;
    const windowMs = end.getTime() - start.getTime();
    const now = dayjs();
    const newStart = dayjs(now.valueOf() - windowMs);
    onRangeChange([newStart, now]);
  }, [pickerValue, start, end, onRangeChange]);

  const handlePreset = useCallback(
    (durationMs: number) => {
      if (!pickerValue[1]) return;
      const newEnd = pickerValue[1];
      const newStart = dayjs(newEnd.valueOf() - durationMs);
      onRangeChange([newStart, newEnd]);
    },
    [pickerValue, onRangeChange]
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <SafeDateTimeRangePicker
          value={pickerValue}
          onChange={onRangeChange}
          timezone={resolvedTimeZone}
          slotProps={{ textField: { size: "small" } }}
          format="YYYY-MM-DD HH:mm:ss"
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => {
              handleShiftWindow(-1);
            }}
            size="small"
          >
            <ArrowBackIosNewIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            onClick={() => {
              handleShiftWindow(1);
            }}
            size="small"
          >
            <ArrowForwardIosIcon fontSize="inherit" />
          </IconButton>
          <Button size="small" onClick={handleJumpToNow}>
            {t("jumpToNow")}
          </Button>
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          {WINDOW_PRESETS.map((preset) => {
            const isActive =
              end.getTime() - start.getTime() === preset.durationMs;
            return (
              <Button
                key={preset.id}
                variant={isActive ? "contained" : "outlined"}
                size="small"
                onClick={() => {
                  handlePreset(preset.durationMs);
                }}
              >
                {t(preset.labelKey)}
              </Button>
            );
          })}
          <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
          <TimezoneToggleButton />
        </Stack>
      </Stack>
    </Paper>
  );
}
